import InformationIcon from "@mui/icons-material/ErrorOutlineOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { TFunction } from "i18next";
import Tooltip from "components/shared/Tooltip";
import { IProjectData } from "hooks/api/GQL/project/useProjectData.models";
import { DecisionStatus } from "utils/constants/healthAuthorities.constants";
import { ProjectStatus } from "utils/constants/project.constants";

const getShouldBeAbleToComplete = (project: IProjectData) => {
  const { referenceHA, relianceParticipants } = project.metadata;

  const hasReferenceHAOrganization = project.configuration.hasReferenceHA;

  const hasReferenceHANecessaryData =
    !hasReferenceHAOrganization ||
    (!!referenceHA?.decisionDate &&
      !!referenceHA.decisionStatus &&
      referenceHA.decisionStatus !== DecisionStatus.Pending);

  const hasAllRelianceParticipantsNecessaryData = relianceParticipants?.every(
    (participant) =>
      !!participant.decisionDate &&
      !!participant.decisionStatus &&
      participant.decisionStatus !== DecisionStatus.Pending,
  );

  return hasReferenceHANecessaryData && hasAllRelianceParticipantsNecessaryData;
};

const prevStatusOption = (project: IProjectData, t: TFunction) => {
  return {
    id: project.previousStatus!,
    label: t(`status.project.${project.previousStatus}`, { ns: "common" }),
    adornment: (
      <Tooltip title={t(`project.statusCanBeRevertedToPrevState`)}>
        <InformationIcon color="action" fontSize="small" />
      </Tooltip>
    ),
  };
};

const getOptionsFromStatuses = (statuses: ProjectStatus[], t: TFunction) =>
  statuses.map((status) => ({
    id: status,
    label: t(`status.project.${status}`, { ns: "common" }),
  }));

export const getRelianceProjectStatuses = (
  project: IProjectData | undefined,
  t: TFunction,
) => {
  switch (project?.status) {
    case ProjectStatus.Planned:
      return getOptionsFromStatuses([ProjectStatus.Cancelled], t);

    case ProjectStatus.InProgress:
      const isCompletedDisabled = !getShouldBeAbleToComplete(project);

      const completeStatusOption = {
        id: ProjectStatus.Completed,
        label: t(`status.project.${ProjectStatus.Completed}`, { ns: "common" }),
        disabled: isCompletedDisabled,
        adornment: isCompletedDisabled ? (
          <Tooltip title={t(`project.statusCantBeInCompletedState`)}>
            <WarningAmberIcon color="warning" fontSize="small" />
          </Tooltip>
        ) : null,
      };

      return [
        completeStatusOption,
        ...getOptionsFromStatuses([ProjectStatus.Cancelled], t),
      ];

    case ProjectStatus.Cancelled:
      return [prevStatusOption(project, t)];

    default:
      return [];
  }
};

export const getProjectStatuses = (
  project: IProjectData | undefined,
  t: TFunction,
) => {
  switch (project?.status) {
    case ProjectStatus.Planned:
      return getOptionsFromStatuses([ProjectStatus.Cancelled], t);

    case ProjectStatus.InProgress:
      return getOptionsFromStatuses(
        [ProjectStatus.Completed, ProjectStatus.Cancelled],
        t,
      );

    case ProjectStatus.Cancelled:
      return [prevStatusOption(project, t)];

    default:
      return [];
  }
};
